// DO NOT ADD TO THIS FILE
// This predates the our code generation being able to map the correct URL to RPC call.
// Instead, add to `libs/shared/util-protos/config.js`.

import { VerifyPasswordRequest, VerifyPasswordResponse } from '@cash-web/protos/squareup/cash/security/api.pb'
import { SkipBlockerRequest, SkipBlockerResponse } from '@cash-web/protos/squareup/franklin/app/blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { getFlow, setOnboardingContext as setOnboardingContextBase } from '@cash-web/shared-data-access-api/api'
import { createReactQueryHook, createReactQueryMutationHook } from '@cash-web/shared-data-access-api/types'
import { CashRequestOptions, createSingleUseFlowToken, HttpError, post } from '@cash-web/shared-util-fetch'

import { hasGrantFlowIntent } from './scope'

export const basePath = '/2.0/cash'

// Encoded values for the onboarding context sent in the onboarding payload.
// This is the value of your populated OnboardingDeepLinkPayload proto encoded to string. See https://github.com/squareup/cash-server/blob/main/shadow/loads/usher/src/main/kotlin/com/squareup/cash/shadow/loads/usher/SetOnboardingContextAction.kt
export enum OnboardingContext {
  WEB_ENFORCE_ONBOARDING = 'CAU=',
  CASH_LOCAL = 'CAc=',
  OON_P2P = 'CAg=',
  GRANT_FLOW = 'CAk=',
}

// Exported for testing
export const apiModule = {
  setOnboardingContext: setOnboardingContextBase,
}

export const setOnboardingContext = (context: OnboardingContext, options?: CashRequestOptions) =>
  apiModule.setOnboardingContext(
    {
      onboarding_payload: context,
    },
    options
  )

export const trySetOnboardingContext = async (options?: CashRequestOptions) => {
  const contextPayload = window.contextPayload
  const context = window.context
  try {
    if (contextPayload != null && contextPayload !== '' && !contextPayload.includes('{{')) {
      await apiModule.setOnboardingContext({ onboarding_payload: contextPayload }, options)
    } else if (context != null && context !== '' && context in OnboardingContext) {
      await setOnboardingContext(OnboardingContext[context as keyof typeof OnboardingContext], options)
    } else if (hasGrantFlowIntent()) {
      await setOnboardingContext(OnboardingContext.GRANT_FLOW, options)
    }
  } catch (e) {
    const error = e as HttpError
    logger.error(
      new Error(
        `Failed to set onboarding context to ${contextPayload ? '[contextPayload]' : context} status:${
          error.response?.status
        } reason:${error.message}`
      )
    )
  }
}

export const getOnboardingWithContext = async (
  { context, flowToken = createSingleUseFlowToken() }: { context: OnboardingContext; flowToken?: string },
  options?: CashRequestOptions
) => {
  const { initiation_data } = await setOnboardingContext(context, { ...options, flowToken })
  return getFlow(
    {
      initiation_data,
    },
    { ...options }
  )
}
/**
 * Custom query to combine setting onboarding context (setOnboardingContext) first
 * and retrieving onboarding the scenario plan (getScenarioPlan) after
 */
export const useQueryGetOnboardingWithContext = createReactQueryHook(
  'useQueryGetOnboardingWithContext',
  getOnboardingWithContext
)

/**
 *
 * @deprecated used by blocker flow controller, is being replace by use flow.
 */
export const SkipBlocker = (data: SkipBlockerRequest, config?: CashRequestOptions): Promise<SkipBlockerResponse> =>
  post<SkipBlockerRequest, SkipBlockerResponse>(`${basePath}/skip-blocker`, data, config)

// this api is still not supported by the proto generation, so we have to manually create it
// it is intentionally not exported because we want to avoid using the api by itself
const verifyPassword = (data: VerifyPasswordRequest, config?: CashRequestOptions): Promise<VerifyPasswordResponse> =>
  post<VerifyPasswordRequest, VerifyPasswordResponse>(`/cash-app/security/1.0/verify-password`, data, config)

export const useMutationVerifyPassword = createReactQueryMutationHook('useMutationVerifyPassword', verifyPassword)
